// Libs
import { TFunction } from 'i18next';
import * as yup from 'yup';
// Others
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { TypeDiscountEnum } from '~/utils/enum/common';
import { dataTimeZone } from '~/utils/helper/common';
import { IDiscountJob, IFormJob, IMaterialsJob, IServiceJob, ITaskJob } from '~/utils/interface/job';

export const schema = (t: TFunction) => {
  return yup.object().shape({
    name: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    clientId: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    address: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    address2: yup.string().trim().optional(),
    city: yup.string().trim().optional(),
    state: yup.string().trim().optional(),
    zipCode: yup.string().trim().optional(),
    country: yup.string().trim().optional(),
    latitude: yup.number().optional(),
    longitude: yup.number().optional(),
    startDate: yup.string().trim().optional(),
    endDate: yup.string().trim().optional(),
    startTime: yup.string().trim().optional(),
    endTime: yup.string().trim().optional(),
    timezone: yup.string().trim().optional(),
    assigneeIds: yup
      .array()
      .of(yup.string().required(t('common_validate_field_required', { ns: 'common/base' })))
      .optional(),
    commissionRate: yup
      .number()
      .optional()
      .max(100, t('common_validate_max_percent', { ns: 'common/base' })),
    type: yup.string().trim().optional(),
    unit: yup.string().trim().optional(),
    notes: yup.string().trim().optional(),
    callback: yup.boolean().optional(),
    leadSourceId: yup.string().trim().optional(),
    jobTagIds: yup
      .array()
      .of(yup.string().required(t('common_validate_field_required', { ns: 'common/base' })))
      .optional(),
    jobServices: yup
      .array()
      .of(
        yup.object().shape({
          serviceId: yup
            .string()
            .trim()
            .test(
              'serviceId-required',
              t('common_validate_field_required', { ns: 'common/base' }),
              function (value) {
                const { serviceId } = this.parent;
                if (serviceId && serviceId.trim() !== '') {
                  if (!value || value.trim() === '') {
                    return this.createError({
                      message: t('common_validate_field_required', { ns: 'common/base' }),
                    });
                  }
                }
                return true;
              }
            ),
          price: yup
            .string()
            .trim()
            .when('serviceId', {
              is: (value: string) => value && value.trim() !== '',
              then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
              otherwise: (schema) => schema.optional(),
            }),
          quantity: yup
            .string()
            .trim()
            .when('serviceId', {
              is: (value: string) => value && value.trim() !== '',
              then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
              otherwise: (schema) => schema.optional(),
            }),
          cost: yup
            .string()
            .trim()
            .when('serviceId', {
              is: (value: string) => value && value.trim() !== '',
              then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
              otherwise: (schema) => schema.optional(),
            }),
          description: yup.string().trim().optional(),
          totalPrice: yup.string().trim().optional(),
        })
      )
      .optional(),
    jobMaterials: yup
      .array()
      .of(
        yup.object().shape({
          materialId: yup
            .string()
            .trim()
            .test(
              'materialId-required',
              t('common_validate_field_required', { ns: 'common/base' }),
              function (value) {
                const { materialId } = this.parent;
                if (materialId && materialId.trim() !== '') {
                  if (!value || value.trim() === '') {
                    return this.createError({
                      message: t('common_validate_field_required', { ns: 'common/base' }),
                    });
                  }
                }
                return true;
              }
            ),
          price: yup
            .string()
            .trim()
            .when('materialId', {
              is: (value: string) => value && value.trim() !== '',
              then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
              otherwise: (schema) => schema.optional(),
            }),
          quantity: yup
            .string()
            .trim()
            .when('materialId', {
              is: (value: string) => value && value.trim() !== '',
              then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
              otherwise: (schema) => schema.optional(),
            }),
          cost: yup
            .string()
            .trim()
            .when('materialId', {
              is: (value: string) => value && value.trim() !== '',
              then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
              otherwise: (schema) => schema.optional(),
            }),
          description: yup.string().trim().optional(),
          totalPrice: yup.string().trim().optional(),
        })
      )
      .optional(),
    discounts: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            type: yup
              .string()
              .trim()
              .test(
                'type-required',
                t('common_validate_field_required', { ns: 'common/base' }),
                function (value) {
                  const { type } = this.parent;
                  if (type && type.trim() !== '') {
                    if (!value || value.trim() === '') {
                      return this.createError({
                        message: t('common_validate_field_required', { ns: 'common/base' }),
                      });
                    }
                  }
                  return true;
                }
              ),
            rate: yup.string().when('type', {
              is: (value: string) => value && value.trim() !== '',
              then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
              otherwise: (schema) => schema.optional(),
            }),
            description: yup.string().trim().optional(),
          })
          .test(
            'require-all-if-any',
            t('common_validate_field_required', { ns: 'common/base' }),
            function (value) {
              const { type, rate } = value || {};
              const hasAny = !!(type || rate);
              const allFilled = !!(type && rate);
              if (hasAny && !allFilled) {
                return this.createError({
                  message: t('common_validate_field_required', { ns: 'common/base' }),
                });
              }
              return true;
            }
          )
      )
      .optional(),
    subtotal: yup.number().optional(),
    total: yup.number().optional(),
    tasks: yup
      .array()
      .of(
        yup.object().shape({
          name: yup
            .string()
            .trim()
            .test(
              'name-required',
              t('common_validate_field_required', { ns: 'common/base' }),
              function (value) {
                const { name } = this.parent;
                if (name && name.trim() !== '') {
                  if (!value || value.trim() === '') {
                    return this.createError({
                      message: t('common_validate_field_required', { ns: 'common/base' }),
                    });
                  }
                }
                return true;
              }
            ),
          assigneeId: yup.string().trim().optional(),
          dueDate: yup.string().trim().optional(),
          description: yup.string().trim().optional(),
          status: yup.string().trim().optional(),
        })
      )
      .optional(),
    folders: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().optional(),
          isCurrentApply: yup.boolean().optional(),
          files: yup
            .array()
            .of(
              yup.object().shape({
                url: yup.string().optional(),
                fieldName: yup.string().optional(),
                size: yup.number().optional(),
                mimetype: yup.string().optional(),
                name: yup.string().optional(),
              })
            )
            .optional(),
        })
      )
      .optional(),
  });
};

export const defaultValuesForm: IFormJob = {
  name: EMPTY_STRING,
  clientId: EMPTY_STRING,
  address: EMPTY_STRING,
  address2: EMPTY_STRING,
  city: EMPTY_STRING,
  state: EMPTY_STRING,
  zipCode: EMPTY_STRING,
  country: EMPTY_STRING,
  latitude: DEFAULT_NUMBER_ZERO,
  longitude: DEFAULT_NUMBER_ZERO,
  startDate: EMPTY_STRING,
  endDate: EMPTY_STRING,
  startTime: EMPTY_STRING,
  endTime: EMPTY_STRING,
  timezone: dataTimeZone(),
  assigneeIds: [],
  commissionRate: DEFAULT_NUMBER_ZERO,
  type: EMPTY_STRING,
  unit: EMPTY_STRING,
  notes: EMPTY_STRING,
  callback: false,
  leadSourceId: EMPTY_STRING,
  jobTagIds: [],
  jobServices: [
    {
      serviceId: EMPTY_STRING,
      price: EMPTY_STRING,
      quantity: EMPTY_STRING,
      cost: EMPTY_STRING,
      description: EMPTY_STRING,
      totalPrice: EMPTY_STRING,
    },
  ],
  jobMaterials: [
    {
      materialId: EMPTY_STRING,
      price: EMPTY_STRING,
      cost: EMPTY_STRING,
      quantity: EMPTY_STRING,
      description: EMPTY_STRING,
      totalPrice: EMPTY_STRING,
    },
  ],
  discounts: [{ description: EMPTY_STRING, type: EMPTY_STRING, rate: EMPTY_STRING }],
  subtotal: DEFAULT_NUMBER_ZERO,
  total: DEFAULT_NUMBER_ZERO,
  tasks: [
    {
      title: EMPTY_STRING,
      assigneeId: EMPTY_STRING,
      dueDate: EMPTY_STRING,
      description: EMPTY_STRING,
      status: EMPTY_STRING,
    },
  ],
};

export const defaultValuesFormService: IServiceJob = {
  serviceId: EMPTY_STRING,
  price: EMPTY_STRING,
  quantity: EMPTY_STRING,
  cost: EMPTY_STRING,
  description: EMPTY_STRING,
  totalPrice: EMPTY_STRING,
};

export const defaultValuesFormMaterial: IMaterialsJob = {
  materialId: EMPTY_STRING,
  price: EMPTY_STRING,
  cost: EMPTY_STRING,
  quantity: EMPTY_STRING,
  description: EMPTY_STRING,
  totalPrice: EMPTY_STRING,
};

export const defaultValuesFormDiscount: IDiscountJob = {
  type: TypeDiscountEnum.DOLLAR,
  rate: EMPTY_STRING,
  description: EMPTY_STRING,
};

export const defaultValuesFormTask: ITaskJob = {
  title: EMPTY_STRING,
  assigneeId: EMPTY_STRING,
  dueDate: EMPTY_STRING,
  description: EMPTY_STRING,
};

export const convertDataUpdate = <T extends { id?: string }>(
  current: T[] = [],
  initial: T[] = []
): {
  updated: T[];
  created: T[];
  deleted: string[];
} => {
  const updated: T[] = [];
  const created: T[] = [];

  current.forEach((item) => {
    if (item.id) {
      updated.push(item);
    } else {
      created.push(item);
    }
  });

  const currentIds = current.filter((i) => i.id).map((i) => i.id as string);
  const deleted = initial.filter((i) => i.id && !currentIds.includes(i.id)).map((i) => i.id!);

  return { updated, created, deleted };
};
